import { useGetEnrollmentJointContactInfoData, useSubmitEnrollmentJointContactInfoData } from '@egw/api'
import {
  Input,
  Button,
  Alert,
  AlertType,
  GenericError,
  ProgressBar,
  Checkbox,
  Link,
  Tag,
  ModalDrawer,
} from '@egw/components'
import { configClient } from '@egw/config'
import { WEALTH_ROUTES } from '@egw/constants'
import { useTranslation } from '@egw/i18n'
import { EnrollmentJointLayout } from '@egw/layouts'
import { Logger } from '@egw/logger'
import { screenRecordingService } from '@egw/screen-recording'
import { UserIcon } from '@heroicons/react/16/solid'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import { useEffect, useState, type FC } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useNavigate } from 'react-router'
import { EnrollmentPageLoading } from '../../../components/page-loading'
import { PageHeader } from '../../../components'
import { ROUTES } from '../../../constants/routes/routes-generic'
import { newPathIsDifferent } from '../../../utils/general/new-path-is-different'
import { useValidateRoute } from '../../../hooks/navigation/validate-route'
import { useHandleEnrollmentErrors } from '../../../utils/joint/api-errors/enrollment-error-handler'
import { type Config } from '../../../config/config'
import { getTestId, HtmlTestTypes } from '../../../utils/testing/get-test-id'
import {
  formatPhoneNumber,
  formatPhoneNumberWwithMask,
  preformatPhoneNumber,
} from '../../../utils/phone-number/phone-number'
import { getZendeskAPIValue } from '../../../utils/zendesk/get-zendesk-api-value'
import { prettifyJointAccountType } from '../../../utils/general/prettify-joint-account-type'
import { JOINT_ACCOUNT_TYPE } from '../../../constants/joint-account-types'
import { ApexDisclosures } from '../../../components/get-started-enrollment-disclosures/apex-disclosures'
import { getNameValidationRule } from '../../../utils/form-validation'

const log = new Logger('Enrollment - Joint - ContactInfo')

interface FormData {
  email: string
  firstName: string
  lastName: string
  phoneNumber: string

  jointFirstName: string
  jointLastName: string
  jointPhoneNumber: string
  jointEmail: string
}

export const ContactInfoPage: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const navigate = useNavigate()
  const [isJtwrosModalOpen, setIsJtwrosModalOpen] = useState(false)
  const [isTicModalOpen, setIsTicModalOpen] = useState(false)
  const [isCommunityPropertyModalOpen, setIsCommunityPropertyModalOpen] = useState(false)
  const [isJointTenantsEntiretyModalOpen, setIsJointTenantsEntiretyModalOpen] = useState(false)

  const config = configClient.getConfig() as Config

  // Get session recording url for FullStory
  const sessionRecordingUrl = screenRecordingService.getSessionUrl()

  useEffect(() => {
    // sendEvent(/* TODO: ADD EVENT FOR LANDING ON PAGE */)
  }, [])

  const { data, isLoading, error } = useGetEnrollmentJointContactInfoData()
  const { mutate, isLoading: isSubmitting, error: submitError } = useSubmitEnrollmentJointContactInfoData()

  // Log initial data loading errors
  useEffect(() => {
    if (error) {
      log.error('Error loading enrollment joint contact info data', error)
    }
  }, [error])

  // Log form submission errors
  useEffect(() => {
    if (submitError) {
      log.error('Error submitting enrollment joint contact info form', submitError)
    }
  }, [submitError])

  const progress = data?.activePerson.progress ?? undefined

  // Protect route
  useValidateRoute(data?.activePerson.path)

  // Error handler hook
  const handleEnrollmentErrors = useHandleEnrollmentErrors('Contact info')

  // AUTO GENERATED FORM HANDLERS START
  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors: formErrors, isValid, touchedFields },
  } = useForm({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      agreements: undefined,

      jointFirstName: '',
      jointLastName: '',
      jointPhoneNumber: '',
      jointEmail: '',
    },
    mode: 'onChange',
  })

  // Update form values when data is loaded
  useEffect(() => {
    if (data?.activePerson) {
      reset({
        email: data.activePerson.email ?? '',
        firstName: data.activePerson.name?.firstName ?? '',
        lastName: data.activePerson.name?.lastName ?? '',
        phoneNumber: formatPhoneNumber(data.activePerson.phone?.phoneNumber ?? ''),
        // TODO: update jointFirstName etc to right names
        jointFirstName: data.jointPerson.name?.firstName ?? '',
        jointLastName: data.jointPerson.name?.lastName ?? '',
        jointPhoneNumber: formatPhoneNumber(data.jointPerson.phone?.phoneNumber ?? ''),
        jointEmail: data.jointPerson.email ?? '',
        agreements: undefined,
      })
    }
  }, [data, reset])

  const onSubmit = handleSubmit(async (fData: unknown): Promise<void> => {
    const formData = fData as FormData
    if (isValid) {
      try {
        const response = await mutate({
          activePerson: {
            metadata: {
              sessionRecordingUrl,
            },
            triggerEmailOtp: true,
            email: formData.email,
            name: {
              firstName: formData.firstName,
              lastName: formData.lastName,
            },
            phone: {
              phoneNumber: preformatPhoneNumber(formData.phoneNumber),
              countryPhoneCode: 1,
            },
            privacyPolicy: {
              id: getZendeskAPIValue(config.zendeskArticles.privacyPolicy),
              accepted: true,
            },
            eSignConsent: {
              id: getZendeskAPIValue(config.zendeskArticles.eSignature),
              accepted: true,
            },
          },
          jointPerson: {
            email: formData.jointEmail,
            name: {
              firstName: formData.jointFirstName,
              lastName: formData.jointLastName,
            },
            phone: {
              phoneNumber: preformatPhoneNumber(formData.jointPhoneNumber),
              countryPhoneCode: 1,
            },
          },
        })

        // Check for API errors in the response
        if (response && handleEnrollmentErrors(response)) {
          return
        }

        // If path exists and is different
        if (response?.activePerson.path && newPathIsDifferent(location.pathname, response.activePerson.path)) {
          void navigate(response.activePerson.path)
        } else if (!response?.activePerson.path) {
          // If no path from BE, redirect to error
          log.error('No path returned from backend, redirecting to error page')
          void navigate(`${WEALTH_ROUTES.ENROLLMENT.path}/${ROUTES.ERROR.path}`)
        }
      } catch (e) {
        log.error('Error submitting contact info form', e)
      }
    }
  })
  // AUTO GENERATED FORM HANDLERS END
  const isInitiator = data?.activePerson.isInitiator
  const title = isInitiator ? 'joint.contact_info.page_title' : 'joint.contact_info.page_title_p2'
  const profileName = data?.activePerson.name?.firstName ? `${data.activePerson.name.firstName}'s Profile` : ''
  const yourInformation = isInitiator ? 'joint.contact_info.your_information' : 'joint.contact_info.your_information_p2'
  const accountType = data?.accountType
  const accountTypePrettified = prettifyJointAccountType(accountType ?? '')

  // Validation helper functions
  const validatePhoneNotDuplicate = (value: string): string | true => {
    if (!value) return true

    if (isInitiator) {
      // When initiator, check against the joint user's phone they're entering
      const jointPhoneNumber = getValues('jointPhoneNumber') as string | undefined
      if (jointPhoneNumber && preformatPhoneNumber(value) === preformatPhoneNumber(jointPhoneNumber)) {
        return t('joint.contact_info.contact_info_cannot_match')
      }
    } else {
      // When not initiator, check against the initiator's phone from API data
      const initiatorPhone = data?.jointPerson.phone?.phoneNumber
      const initiatorCountryCode = data?.jointPerson.phone?.countryPhoneCode
      if (initiatorPhone && initiatorCountryCode === 1 && preformatPhoneNumber(value) === initiatorPhone) {
        return t('joint.contact_info.contact_info_cannot_match')
      }
    }
    return true
  }

  const validateEmailNotDuplicate = (value: string): string | true => {
    if (!value) return true

    if (isInitiator) {
      // When initiator, check against the joint user's email they're entering
      const jointEmail = getValues('jointEmail') as string | undefined
      if (jointEmail && value.toLowerCase() === jointEmail.toLowerCase()) {
        return t('joint.contact_info.contact_info_cannot_match')
      }
    } else {
      // When not initiator, check against the initiator's email from API data
      const initiatorEmail = data?.jointPerson.email
      if (initiatorEmail && value.toLowerCase() === initiatorEmail.toLowerCase()) {
        return t('joint.contact_info.contact_info_cannot_match')
      }
    }
    return true
  }

  const validateJointPhoneNotDuplicate = (value: string): string | true => {
    if (!value) return true
    const phoneNumber = getValues('phoneNumber') as string | undefined
    if (phoneNumber && preformatPhoneNumber(value) === preformatPhoneNumber(phoneNumber)) {
      return t('joint.contact_info.contact_info_cannot_match')
    }
    return true
  }

  const validateJointEmailNotDuplicate = (value: string): string | true => {
    if (!value) return true
    const email = getValues('email') as string | undefined
    if (email && value.toLowerCase() === email.toLowerCase()) {
      return t('joint.contact_info.contact_info_cannot_match')
    }
    return true
  }

  return (
    <EnrollmentJointLayout title={t(title, { name: data?.activePerson.name?.firstName ?? '' })}>
      {/* JTWROS MODAL */}
      <ModalDrawer
        isOpen={isJtwrosModalOpen}
        onClose={() => {
          setIsJtwrosModalOpen(false)
        }}
        title={t('joint.account_type.modals.jtwros.title')}
        testId="account-type-modal"
      >
        <div className="flex flex-col gap-md mt-sm">
          <ul className="list-disc list-inside space-y-2">
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.jtwros.description_1')}</li>
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.jtwros.description_2')}</li>
          </ul>
        </div>
      </ModalDrawer>
      {/* TIC MODAL */}
      <ModalDrawer
        isOpen={isTicModalOpen}
        onClose={() => {
          setIsTicModalOpen(false)
        }}
        title={t('joint.account_type.modals.tic.title')}
        testId="account-type-modal"
      >
        <div className="flex flex-col gap-md mt-sm">
          <ul className="list-disc list-inside space-y-2">
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.tic.description_1')}</li>
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.tic.description_2')}</li>
            <li className="typo-primary-base text-text-body">{t('joint.account_type.modals.tic.description_3')}</li>
          </ul>
        </div>
      </ModalDrawer>
      {/* COMMUNITY PROPERTY MODAL */}
      <ModalDrawer
        isOpen={isCommunityPropertyModalOpen}
        onClose={() => {
          setIsCommunityPropertyModalOpen(false)
        }}
        title={t('joint.account_type.modals.community_property.title')}
        testId="account-type-modal"
      >
        <div className="flex flex-col gap-md mt-sm">
          <ul className="list-disc list-inside space-y-2">
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.community_property.description_1')}
            </li>
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.community_property.description_2')}
            </li>
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.community_property.description_3')}
            </li>
          </ul>
        </div>
      </ModalDrawer>
      {/* JOINT TENANTS BY THE ENTIRETY MODAL */}
      <ModalDrawer
        isOpen={isJointTenantsEntiretyModalOpen}
        onClose={() => {
          setIsJointTenantsEntiretyModalOpen(false)
        }}
        title={t('joint.account_type.modals.joint_tenants_by_the_entirety.title')}
        testId="account-type-modal"
      >
        <div className="flex flex-col gap-md mt-sm">
          <ul className="list-disc list-inside space-y-2">
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.joint_tenants_by_the_entirety.description_1')}
            </li>
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.joint_tenants_by_the_entirety.description_2')}
            </li>
            <li className="typo-primary-base text-text-body">
              {t('joint.account_type.modals.joint_tenants_by_the_entirety.description_3')}
            </li>
          </ul>
        </div>
      </ModalDrawer>
      {/* Loading initial data to make sure the user has access to this screen and retreive data based on user enrollment application */}
      {isLoading ? <EnrollmentPageLoading testId="contact_info-loading" /> : null}

      {/* In some cases initial data can be null, so we assume if it's not loading and there's no error - we show the page */}
      {!isLoading && !error ? (
        <form onSubmit={(e) => void onSubmit(e)} className="w-full flex-1 flex flex-col justify-start">
          {!isInitiator ? (
            <Tag icon={<UserIcon className="text-olive-400" />} className="mb-xs">
              {profileName}
            </Tag>
          ) : null}
          <PageHeader
            title={t(title, { name: data?.activePerson.name?.firstName ?? '' })}
            // backPath={isInitiator ? `${WEALTH_ROUTES.ENROLLMENT.path}/${JOINT_ROUTES.ACCOUNT_TYPE.path}` : undefined}
          />
          <ProgressBar className="mb-md" progressPercentage={progress} showAnimation />
          {isInitiator ? (
            <>
              <p className="typo-primary-base text-text-meta mb-md" data-test-id="description_1">
                {t('joint.contact_info.description_1')}
              </p>
              <p className="typo-primary-base text-text-meta mb-xl" data-test-id="description_2">
                {t('joint.contact_info.description_2_yeet_coastal')}
              </p>
            </>
          ) : null}

          <p className="typo-primary-base-semibold text-black mb-sm">{t(yourInformation)}</p>
          {!isInitiator ? (
            <p className="typo-primary-base text-text-meta mb-xl" data-test-id="description_2">
              {t('joint.contact_info.your_information_description_p2')}
            </p>
          ) : null}
          <div className="flex flex-col md:flex-row md:gap-md">
            <Controller
              name="firstName"
              control={control}
              rules={{
                required: t('common.required_field'),
                ...getNameValidationRule(t('joint.contact_info.first_name_error')),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('joint.contact_info.first_name_label')}
                  required
                  error={touchedFields.firstName ? formErrors.firstName?.message : undefined}
                  type="text"
                  className="mb-sm w-full"
                  testId={getTestId('firstName', HtmlTestTypes.input)}
                  isPII
                />
              )}
            />
            <Controller
              name="lastName"
              control={control}
              rules={{
                required: t('common.required_field'),
                ...getNameValidationRule(t('joint.contact_info.last_name_error')),
              }}
              render={({ field }) => (
                <Input
                  {...field}
                  label={t('joint.contact_info.last_name_label')}
                  required
                  error={touchedFields.lastName ? formErrors.lastName?.message : undefined}
                  type="text"
                  className="mb-sm w-full"
                  testId={getTestId('lastName', HtmlTestTypes.input)}
                  isPII
                />
              )}
            />
          </div>

          <Controller
            name="phoneNumber"
            control={control}
            rules={{
              required: t('common.required_field'),
              pattern: {
                value: /^\+1 \(\d{3}\) \d{3}-\d{4}$/i,
                message: t(`joint.contact_info.invalid_phone_number`),
              },
              validate: validatePhoneNotDuplicate,
            }}
            render={({ field }) => (
              <>
                <Input
                  {...field}
                  label={t(`joint.contact_info.phone_number`)}
                  onInput={(e) => {
                    formatPhoneNumberWwithMask(e)
                  }}
                  value={formatPhoneNumber(field.value)}
                  required
                  testId="phoneNumber_input"
                  error={touchedFields.phoneNumber ? formErrors.phoneNumber?.message : undefined}
                  isPII
                />
                <p className="typo-primary-sm-regular text-text-meta mt-2xs mb-sm">
                  {t('joint.contact_info.phone_number_helper')}
                </p>
              </>
            )}
          />

          <Controller
            name="email"
            control={control}
            rules={{
              required: t('common.required_field'),
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]/i,
                message: t('common.invalid_email'),
              },
              validate: validateEmailNotDuplicate,
            }}
            render={({ field }) => (
              <Input
                {...field}
                label={t('joint.contact_info.email_label')}
                required
                error={touchedFields.email ? formErrors.email?.message : undefined}
                autoComplete="email"
                type="email"
                className="mb-xl"
                testId={getTestId('email', HtmlTestTypes.input)}
                isPII
              />
            )}
          />

          {/* Joint Account Holder's Information */}
          {isInitiator ? (
            <>
              <p className="typo-primary-base-semibold text-black mb-sm">{t('joint.contact_info.joint_information')}</p>
              <p className="typo-primary-base text-text-meta mb-sm">
                {t('joint.contact_info.joint_information_description')}
              </p>

              <div className="flex flex-col md:flex-row md:gap-md">
                <Controller
                  name="jointFirstName"
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                    ...getNameValidationRule(t('joint.contact_info.first_name_error')),
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label={t('joint.contact_info.first_name_label')}
                      required
                      error={touchedFields.jointFirstName ? formErrors.jointFirstName?.message : undefined}
                      type="text"
                      className="mb-sm w-full"
                      testId={getTestId('jointFirstName', HtmlTestTypes.input)}
                      isPII
                    />
                  )}
                />
                <Controller
                  name="jointLastName"
                  control={control}
                  rules={{
                    required: t('common.required_field'),
                    ...getNameValidationRule(t('joint.contact_info.last_name_error')),
                  }}
                  render={({ field }) => (
                    <Input
                      {...field}
                      label={t('joint.contact_info.last_name_label')}
                      required
                      error={touchedFields.jointLastName ? formErrors.jointLastName?.message : undefined}
                      type="text"
                      className="mb-sm w-full"
                      testId={getTestId('jointLastName', HtmlTestTypes.input)}
                      isPII
                    />
                  )}
                />
              </div>

              <Controller
                name="jointPhoneNumber"
                control={control}
                rules={{
                  required: t('common.required_field'),
                  pattern: {
                    value: /^\+1 \(\d{3}\) \d{3}-\d{4}$/i,
                    message: t(`joint.contact_info.invalid_phone_number`),
                  },
                  validate: validateJointPhoneNotDuplicate,
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t(`joint.contact_info.phone_number`)}
                    onInput={(e) => {
                      formatPhoneNumberWwithMask(e)
                    }}
                    value={formatPhoneNumber(field.value)}
                    required
                    testId="jointPhoneNumber_input"
                    error={touchedFields.jointPhoneNumber ? formErrors.jointPhoneNumber?.message : undefined}
                    className="mb-sm"
                    isPII
                  />
                )}
              />

              <Controller
                name="jointEmail"
                control={control}
                rules={{
                  required: t('common.required_field'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]/i,
                    message: t('common.invalid_email'),
                  },
                  validate: validateJointEmailNotDuplicate,
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t('joint.contact_info.email_label')}
                    required
                    error={touchedFields.jointEmail ? formErrors.jointEmail?.message : undefined}
                    autoComplete="email"
                    type="email"
                    className="mb-xl"
                    testId={getTestId('jointEmail', HtmlTestTypes.input)}
                    isPII
                  />
                )}
              />
            </>
          ) : null}

          {!isInitiator ? (
            <>
              <p className="typo-primary-base-semibold text-black mb-sm">{t('joint.contact_info.account_type')}</p>
              <ul className="typo-primary-base text-text-meta mb-sm list-disc list-outside pl-md">
                <li className="overflow-visible">
                  <div className="overflow-visible">
                    <span>
                      {t('joint.contact_info.joint_account_brokerage_title').replace('*', '')}
                      :&nbsp;&nbsp;
                    </span>
                    <span>{accountTypePrettified}</span>
                    <InformationCircleIcon
                      className="w-5 h-5 text-text-action stroke-2 cursor-pointer inline-block ml-1 align-top absolute"
                      onClick={() => {
                        switch (accountType) {
                          case JOINT_ACCOUNT_TYPE.TIC:
                            setIsTicModalOpen(true)
                            break
                          case JOINT_ACCOUNT_TYPE.JTWROS:
                            setIsJtwrosModalOpen(true)
                            break
                          case JOINT_ACCOUNT_TYPE.COMMUNITY_PROPERTY:
                            setIsCommunityPropertyModalOpen(true)
                            break
                          case JOINT_ACCOUNT_TYPE.JOINT_TENANTS_BY_THE_ENTIRETY:
                            setIsJointTenantsEntiretyModalOpen(true)
                            break
                          default:
                            // Fallback, should never happen
                            log.error('Invalid account type', accountType)
                            break
                        }
                      }}
                    />
                  </div>
                </li>
              </ul>
              <p className="typo-primary-base-light text-text-body mb-2xl">
                {t('joint.contact_info.account_type_description')}
              </p>
            </>
          ) : null}

          <Controller
            name="agreements"
            control={control}
            rules={{
              required: t('common.required_field'),
            }}
            render={({ field }) => (
              <Checkbox
                {...field}
                required
                type="checkbox"
                className="mb-xl"
                testId={getTestId('agreements', HtmlTestTypes.checkbox)}
              >
                <span className="typo-primary-base mt-[2px]">
                  I agree to the{' '}
                  <Link
                    className="typo-primary-base text-text-body underline font-normal"
                    href={config.zendeskArticles.privacyPolicy}
                  >
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link
                    className="typo-primary-base text-text-body underline font-normal"
                    href={config.zendeskArticles.eSignature}
                  >
                    Electronic Signature Consent
                  </Link>
                  .
                </span>
              </Checkbox>
            )}
          />

          <div className="hidden md:block">
            <Button
              type="submit"
              className="w-full md:w-min mb-5xl"
              isDisabled={!isValid}
              isLoading={isSubmitting}
              testId="primaryAction_button"
            >
              {t('common.continue')}
            </Button>
          </div>

          <div className="block">
            <ApexDisclosures />
          </div>

          <div className="flex-1 md:hidden" />

          {submitError ? (
            <Alert alertType={AlertType.Error} description={t('common.generic_error')} className="mt-sm" />
          ) : null}

          {/* AUTO GENERATED COMPONENTS */}
        </form>
      ) : null}

      <div className="fixed bottom-0 left-0 right-0 z-50 flex justify-center px-lg py-xl bg-white shadow-[0_-2px_4px_-1px_rgba(0,0,0,0.05)] md:hidden">
        <Button
          type="submit"
          className="w-full md:w-min md:mb-5xl"
          isDisabled={!isValid}
          isLoading={isSubmitting}
          testId="primaryAction_button"
          onClick={() => {
            if (isValid) {
              void onSubmit()
            }
          }}
        >
          {t('common.continue')}
        </Button>
      </div>

      {/* This error handles the case when the initial data is not loaded, for example network error */}
      {error ? (
        <div className="flex-1 flex flex-col justify-start md:justify-center">
          <GenericError errorMessage={t('common.generic_error')} />
        </div>
      ) : null}
    </EnrollmentJointLayout>
  )
}
